import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notification } from "antd";
import cuid from "cuid";
import { get, isEqual, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import useScreenJob from "shared/hooks/useScreenJob";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { FIELD_REGISTRY } from "utils/api/graphql/queries/screens";
import { FIELD_IMPORT_SCOPES, YAML_IMPORT_STRATEGIES } from "utils/constants";
import {
  MOCKUP_IMAGE_ERRORS,
  prepareMockupImage,
  preparePictureImage,
} from "utils/helpers/mockup-image";
import {
  createScreenDraft,
  draftCopyOf,
  ensureDraftTarget,
  SCREEN_VERSION_STATUS,
} from "utils/helpers/screenDraft";
import {
  checkOf,
  draftCheckOf,
  hasChecks,
  isStrict,
  orphanChecks,
  withCheck,
  withoutCheck,
  withStrict,
} from "./answer-checks";
import {
  CHIP_KINDS,
  citedSlotIds,
  LEAVE_REASONS,
  useCanvasSelection,
} from "./canvas-selection";
import { generateScreenFromCapture } from "./capture-generation";
import { DEFAULT_STATE_PREVIEW_KEY } from "./catalog";
import { draftErrorOf, errorCodeOf } from "./draft-errors";
import { FIELD_FILTERS, fieldRows } from "./screen-fields";
import { previewZones, ZONE_KINDS } from "./preview-zones";
import {
  GUIDE_STEPS,
  guideForGroup,
  guideForSlot,
  resolveGuide,
} from "./screen-groups";
import useScreenWrites, { written } from "./useScreenWrites";
import { CUSTOM_ENTITY } from "./workshop-field-create";
import { mirrorListSlots } from "./workshop-preview";
import {
  fieldPathOf,
  orderedStates,
  PROPOSAL_KINDS,
  registryLabels,
  reportProblems,
  slotLabel,
  stateWhen,
  WORKSHOP_VIEWS,
} from "./workshop-view";

export const DEFAULT_STATE_KEY = DEFAULT_STATE_PREVIEW_KEY;

const SILENT = { onError: () => undefined };

const UNKNOWN_CONDITION = "UNKNOWN_CONDITION_FIELD";

const SLOT_EDIT_ERRORS = [
  "NOT_FOUND",
  "UNKNOWN_FIELD",
  "FIELD_NOT_WRITABLE",
  "UNKNOWN_ACTION",
  "UNKNOWN_COLLECTION",
  "INVALID_SLOT_EDIT",
];

const remapKey =
  ({ keys, before, after }) =>
  (key) => {
    if (keys.includes(key)) return key;
    const previous = before.find((state) => state.id === key);
    const match = previous ? draftCopyOf(after, previous) : null;
    return match ? match.id : null;
  };

const placeKeyOf = (state) =>
  isNil(state)
    ? DEFAULT_STATE_KEY
    : `${state.order || 0}:${state.name || state.id}`;

const settingsOf = (state) =>
  isNil(state)
    ? null
    : {
        name: state.name || "",
        order: state.order,
        conditions: state.conditions || [],
      };

const done = (message) => notification.success({ message });
const fail = (message) => notification.error({ message });
const inform = (message) => notification.info({ message });

const useScreenWorkshop = ({
  processId,
  title,
  screenVersionId,
  statusId,
  screenId,
  versionNumber,
  htmlContent,
  styleContent,
  slots,
  report,
  states,
  variables,
  formDefinition,
  liveFormDefinition,
  publishedScreen,
  published,
  draftOnly,
  onFormDefinitionChange,
  reload,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const WORKSHOP = t("SCREEN_WORKSHOP", { returnObjects: true });
  const CHAT = WORKSHOP.CONVERSATION;
  const STATES = WORKSHOP.STATES;
  const PREVIEW = WORKSHOP.PREVIEW;
  const FIELDS = WORKSHOP.FIELDS;
  const CONDITIONS =
    t("FORM.POSSIBLE_STATUSES.CONDITIONS", { returnObjects: true }) || {};
  const manualScreen = t("FORM.STATUSES.MANUAL_SCREEN");
  const defaultLabel = STATES.DEFAULT;
  const selection = useCanvasSelection();

  const [view, setView] = useState(WORKSHOP_VIEWS.PREVIEW);
  const [openKey, setOpenKey] = useState(null);
  const [guide, setGuide] = useState(null);
  const [zonesShown, setZonesShown] = useState(true);
  const [fieldFilter, setFieldFilter] = useState(FIELD_FILTERS.ALL);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [code, setCode] = useState({ html: "", css: "" });
  const [codeEdited, setCodeEdited] = useState(false);
  const [savingCode, setSavingCode] = useState(false);
  const [newStateOpen, setNewStateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [applying, setApplying] = useState(false);
  const [batch, setBatch] = useState(0);
  const [detecting, setDetecting] = useState(false);
  const [savingSlot, setSavingSlot] = useState(false);
  const [sampleOn, setSampleOn] = useState(false);
  const [sample, setSample] = useState(null);
  const [sampleRender, setSampleRender] = useState(null);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [sampleKey, setSampleKey] = useState(0);
  const createdStates = useRef(new Map());

  const {
    generateHtml: generateScreen,
    generateState,
    generateStateFromNote,
  } = useScreenJob();

  const [ensureScreenDraft] = useMutation(
    graphql.mutations.ENSURE_SCREEN_DRAFT,
    SILENT,
  );
  const [addScreen] = useMutation(graphql.mutations.ADD_SCREEN);
  const [addScreenVersion] = useMutation(graphql.mutations.ADD_SCREEN_VERSION);
  const [updateState] = useMutation(
    graphql.mutations.UPDATE_SCREEN_STATE,
    SILENT,
  );
  const [detectSlots] = useMutation(graphql.mutations.DETECT_SLOTS);
  const [detectVersion] = useMutation(graphql.mutations.DETECT_SLOTS, SILENT);
  const { writeVersion, writeState, createState } = useScreenWrites();
  const [deleteState] = useMutation(
    graphql.mutations.DELETE_SCREEN_STATE,
    SILENT,
  );
  const [detectForState] = useMutation(
    graphql.mutations.DETECT_SLOTS_FOR_STATE,
    SILENT,
  );
  const [updateSlot] = useMutation(
    graphql.mutations.UPDATE_SCREEN_SLOT,
    SILENT,
  );
  const [uploadImage] = useMutation(
    graphql.mutations.UPLOAD_SCREEN_IMAGE,
    SILENT,
  );
  const [prepareSample] = useMutation(
    graphql.mutations.PREPARE_SCREEN_PREVIEW,
    SILENT,
  );
  const [resetSample] = useMutation(
    graphql.mutations.RESET_SCREEN_PREVIEW,
    SILENT,
  );
  const [importFields] = useMutation(
    graphql.mutations.EXECUTE_PROCESS_FIELDS_IMPORT,
    SILENT,
  );
  const { data: registryData, refetch: refetchRegistry } = useQuery(
    FIELD_REGISTRY,
    {
      variables: processId ? { process: { id: processId } } : {},
      skip: isNil(screenVersionId),
    },
  );
  const registry = useMemo(
    () => get(registryData, "fieldRegistry") || [],
    [registryData],
  );
  const known = useMemo(
    () => (registryData ? registryLabels(registry) : null),
    [registryData, registry],
  );

  const draftTarget = () =>
    ensureDraftTarget({
      ensureScreenDraft,
      screen: { id: screenId },
      version: {
        id: screenVersionId,
        status: published
          ? SCREEN_VERSION_STATUS.PUBLISHED
          : SCREEN_VERSION_STATUS.DRAFT,
      },
      states,
    });

  const pending = generated.length > 0;
  const busy = generating || applying;
  const locked = busy || pending;
  const hasScreen = !isNil(screenVersionId);

  const ordered = useMemo(() => orderedStates(states), [states]);
  const previousStates = useRef(ordered);
  const remappedTo = useRef(null);
  const requestedKey =
    selection.stepId === statusId && !isNil(selection.stateId)
      ? selection.stateId
      : DEFAULT_STATE_KEY;
  const remap = remapKey({
    keys: [DEFAULT_STATE_KEY, ...ordered.map((state) => state.id)],
    before: previousStates.current,
    after: ordered,
  });
  const selectedKey = remap(requestedKey) || DEFAULT_STATE_KEY;
  const active =
    selectedKey === DEFAULT_STATE_KEY
      ? null
      : ordered.find((state) => state.id === selectedKey) || null;
  const activeKey = active ? active.id : DEFAULT_STATE_KEY;
  const activeStateId = active ? active.id : null;
  const shownHtml = active ? active.htmlContent : htmlContent;
  const shownSlots = useMemo(
    () => (active ? get(active, "bindingManifest.slots") || [] : slots || []),
    [active, slots],
  );
  const screenSlots = [
    ...(slots || []),
    ...(states || []).flatMap(
      (state) => get(state, "bindingManifest.slots") || [],
    ),
  ];
  const targets = useMemo(
    () => [
      {
        key: DEFAULT_STATE_KEY,
        placeKey: placeKeyOf(null),
        stateId: null,
        state: null,
        name: defaultLabel,
        html: htmlContent,
        slots: slots || [],
      },
      ...ordered.map((state) => ({
        key: state.id,
        placeKey: placeKeyOf(state),
        stateId: state.id,
        state,
        name: state.name || state.id,
        html: state.htmlContent,
        slots: get(state, "bindingManifest.slots") || [],
      })),
    ],
    [defaultLabel, htmlContent, slots, ordered],
  );

  const nameOfTarget = (key) => {
    if (key === DEFAULT_STATE_KEY) return defaultLabel;
    return get(
      ordered.find((item) => item.id === key),
      "name",
      key,
    );
  };

  const pickState = (key, options) =>
    selection.selectState(
      statusId,
      key === DEFAULT_STATE_KEY ? null : key,
      options,
    );
  const remapRef = useRef(null);
  remapRef.current = { remap, pickState, moveStates: selection.moveStates };

  useEffect(() => {
    const { remap: moved, pickState: pick, moveStates } = remapRef.current;
    const before = previousStates.current;
    previousStates.current = ordered;
    const moves = {};
    for (const state of before) {
      const next = moved(state.id);
      if (!isNil(next) && next !== state.id) moves[state.id] = next;
    }
    if (Object.keys(moves).length > 0) moveStates(statusId, moves);
    if (selectedKey === requestedKey) return;
    if (selectedKey !== DEFAULT_STATE_KEY) {
      remappedTo.current = selectedKey;
      return;
    }
    pick(DEFAULT_STATE_KEY, { cite: false, force: true });
  }, [ordered, selectedKey, requestedKey, statusId]);

  const codeSource = useRef(null);
  codeSource.current = { html: shownHtml || "", css: styleContent || "" };
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    if (remappedTo.current === activeKey) {
      remappedTo.current = null;
      return;
    }
    setSettingsOpen(false);
    if (viewRef.current !== WORKSHOP_VIEWS.CODE) return;
    setCode(codeSource.current);
    setCodeEdited(false);
  }, [activeKey]);

  const writeError = (error, fallback) =>
    get(WORKSHOP, ["ERRORS", draftErrorOf(error)]) || fallback;

  const openView = (next) => {
    setView(next);
    setCodeEdited(false);
    if (next !== WORKSHOP_VIEWS.PREVIEW) setSettingsOpen(false);
    if (next === WORKSHOP_VIEWS.CODE)
      setCode({ html: shownHtml || "", css: styleContent || "" });
  };

  const editCode = (next) => {
    setCode(next);
    setCodeEdited(true);
  };

  const toggleSettings = () => {
    if (settingsOpen) {
      setSettingsOpen(false);
      return;
    }
    setSettings(settingsOf(active));
    setSettingsOpen(true);
  };

  const blockChip = (slotId, label) => {
    const slot = shownSlots.find((item) => item.id === slotId);
    return {
      kind: CHIP_KINDS.BLOCK,
      label: label || (slot ? slotLabel(slot, FIELDS) : slotId),
      stepId: statusId,
      stateId: activeStateId,
      slotId,
    };
  };

  const citeSlot = (slotId) => selection.cite(blockChip(slotId));

  const pickedChip = (slotId, pick = {}) => {
    if (pick.kind === ZONE_KINDS.LIST && pick.list) return blockChip(pick.list);
    if (pick.kind === ZONE_KINDS.CARD && get(pick, "ids.length")) {
      const first = shownSlots.find((item) => item.id === pick.ids[0]);
      return blockChip(
        pick.ids[0],
        [WORKSHOP.GUIDE.CARD_TITLE, first ? slotLabel(first, FIELDS) : null]
          .filter(Boolean)
          .join(" · "),
      );
    }
    return isNil(slotId) ? null : blockChip(slotId);
  };

  const selectSlot = (slotId, pick) => {
    const chip = pickedChip(slotId, pick);
    if (chip) selection.citeOnly(chip);
  };

  const citeProblem = (problem) => {
    const slot = (slots || []).find(
      (item) => fieldPathOf(item) === problem.field,
    );
    if (isNil(slot)) {
      selection.cite({
        kind: CHIP_KINDS.STEP,
        label: [title, problem.field].filter(Boolean).join(" · "),
        stepId: statusId,
      });
      return;
    }
    selection.cite({
      kind: CHIP_KINDS.FIELD,
      label: problem.field,
      stepId: statusId,
      slotId: slot.id,
    });
  };

  const cited = citedSlotIds(selection.chips, {
    stepId: statusId,
    stateId: activeStateId,
  });

  const persistChecks = (next) => {
    onFormDefinitionChange(next);
    return true;
  };

  const removeCheck = (accessor) =>
    persistChecks(withoutCheck(formDefinition, accessor));

  const answers = {
    formDefinition,
    checkFor: (variable) =>
      checkOf(formDefinition, variable) || {
        ...draftCheckOf(null, variable),
        required: false,
        rules: [],
      },
    save: (check) => persistChecks(withCheck(formDefinition, check)),
    onRemove: removeCheck,
    onStrict: (checked) => persistChecks(withStrict(formDefinition, checked)),
    strict: isStrict(formDefinition),
    showStrict: hasChecks(formDefinition),
    orphans: orphanChecks(formDefinition, screenSlots, variables),
  };

  const editSlots = async (places, patch) => {
    if (!screenVersionId || !places.length) return null;
    setSavingSlot(true);
    try {
      const target = await draftTarget();
      let renamed = null;
      for (const place of places) {
        const { data, errors } = await updateSlot({
          variables: {
            screenVersion: { id: target.version.id },
            ...(isNil(place.stateId)
              ? {}
              : { screenState: { id: target.stateIdOf(place.stateId) } }),
            slot: { id: place.slotId, ...patch },
          },
        });
        const result = get(data, "updateScreenSlot");
        if (errors || !result) {
          fail(
            get(FIELDS, [
              "EDIT_ERRORS",
              errorCodeOf(errors, SLOT_EDIT_ERRORS),
            ]) || writeError(errors, CHAT.FIELD_ERROR),
          );
          if (reload) await reload();
          return null;
        }
        renamed = renamed || result.slotId || place.slotId;
      }
      if (reload) await reload();
      return renamed;
    } catch (error) {
      fail(writeError(error, CHAT.FIELD_ERROR));
      return null;
    } finally {
      setSavingSlot(false);
    }
  };

  const variableOfPath = (path) =>
    (variables || []).find((variable) => variable.field === path) || null;

  const moveCheck = (slot, toPath) => {
    const fromPath = fieldPathOf(slot);
    if (isNil(fromPath) || fromPath === toPath) return;
    const previous = checkOf(formDefinition, variableOfPath(fromPath));
    const target = variableOfPath(toPath);
    if (isNil(previous) || isNil(target)) return;
    const stillShown = screenSlots.some(
      (other) => other.id !== slot.id && fieldPathOf(other) === fromPath,
    );
    const base = stillShown
      ? formDefinition
      : withoutCheck(formDefinition, previous.accessor);
    const moved = checkOf(base, target) || {
      ...draftCheckOf(null, target),
      required: previous.required,
    };
    persistChecks(withCheck(base, moved));
  };

  const editRow = async (row, patch) => {
    const renamed = await editSlots(row.places, patch);
    if (isNil(renamed)) return false;
    if (!isNil(patch.field)) moveCheck(row.slot, patch.field);
    if (renamed !== row.slot.id)
      setOpenKey((current) =>
        current === row.key
          ? `${row.key.slice(0, -row.slot.id.length)}${renamed}`
          : current,
      );
    return true;
  };

  const importError = (result, messages) => {
    const first = get(result, "errors.0");
    const template = get(messages, get(first, "key")) || messages.GENERIC;
    return template.replace(
      "{{canonical}}",
      get(first, "params.canonical") || "",
    );
  };

  const saveCustomField = ({ key, label, type, options }, strategy) => {
    const withOptions = options.length > 0;
    return importFields({
      variables: {
        data: {
          process: { id: processId },
          content: JSON.stringify({
            process: processId,
            fields: [
              {
                key,
                label,
                type,
                ...(withOptions ? { referentiel: key } : {}),
              },
            ],
            ...(withOptions ? { referentiels: { [key]: options } } : {}),
          }),
          strategy,
          scope: FIELD_IMPORT_SCOPES.PROCESS,
        },
      },
    }).then(({ data }) => get(data, "executeProcessFieldsImport"));
  };

  const handleCreateField = async (row, field) => {
    const ERRORS = FIELDS.CREATE.ERRORS;
    if (!processId) return ERRORS.GENERIC;
    const result = await saveCustomField(
      field,
      YAML_IMPORT_STRATEGIES.NEW_ONLY,
    ).catch(() => null);
    if (!get(result, "success")) return importError(result, ERRORS);
    const path = `${CUSTOM_ENTITY}.${field.key}`;
    if (get(result, "skipped") > 0)
      return ERRORS.FIELD_EXISTS.replace("{{field}}", path);
    await refetchRegistry();
    if (fieldPathOf(row.slot) !== path) {
      const linked = await editRow(row, { field: path });
      if (!linked) return ERRORS.LINK_FAILED;
    }
    done(CHAT.FIELD_CREATED.replace("{{field}}", path));
    return null;
  };

  const handleEditField = async (row, field) => {
    const ERRORS = { ...FIELDS.CREATE.ERRORS, ...FIELDS.EDIT.ERRORS };
    if (!processId) return ERRORS.GENERIC;
    const result = await saveCustomField(
      field,
      YAML_IMPORT_STRATEGIES.UPDATE_CORE,
    ).catch(() => null);
    if (!get(result, "success")) return importError(result, ERRORS);
    await refetchRegistry();
    const path = `${CUSTOM_ENTITY}.${field.key}`;
    const refreshed = await editRow(row, { field: path });
    if (!refreshed) return ERRORS.SCREEN_NOT_UPDATED;
    done(CHAT.FIELD_UPDATED.replace("{{field}}", path));
    return null;
  };

  const sampleSlotsKey = shownSlots
    .map(
      (slot) =>
        `${slot.id}:${fieldPathOf(slot) || get(slot, "binding.source") || ""}`,
    )
    .join("|");

  const sampleRequest = useRef(0);

  const loadSample = async (request, extra = {}) => {
    if (!statusId) return;
    const token = sampleRequest.current + 1;
    sampleRequest.current = token;
    setSampleBusy(true);
    try {
      const { data } = await request({
        variables: {
          status: { id: statusId },
          ...(screenVersionId
            ? { screenVersion: { id: screenVersionId } }
            : {}),
          ...extra,
        },
      });
      if (sampleRequest.current !== token) return;
      const result =
        get(data, "prepareScreenPreview") || get(data, "resetScreenPreview");
      if (!result) {
        fail(PREVIEW.SAMPLE.ERROR);
        return;
      }
      setSample(result);
      setSampleKey((key) => key + 1);
    } catch (_error) {
      if (sampleRequest.current !== token) return;
      fail(PREVIEW.SAMPLE.ERROR);
    } finally {
      if (sampleRequest.current === token) setSampleBusy(false);
    }
  };
  const loadSampleRef = useRef(loadSample);
  loadSampleRef.current = loadSample;

  useEffect(() => {
    if (!sampleOn || !hasScreen) return undefined;
    loadSampleRef.current(prepareSample);
    return () => {
      sampleRequest.current += 1;
      setSampleBusy(false);
    };
  }, [sampleOn, hasScreen, sampleSlotsKey, prepareSample]);

  const toggleSample = () => {
    setSampleRender(null);
    setSampleOn((current) => !current);
  };

  const handleImageUpload = async (row, file) => {
    if (!screenVersionId || !file) return false;
    setSavingSlot(true);
    let url = null;
    try {
      const image = await preparePictureImage(file);
      const target = await draftTarget();
      const { data } = await uploadImage({
        variables: { screenVersion: { id: target.version.id }, image },
      });
      url = get(data, "uploadScreenImage.url") || null;
    } catch (error) {
      url = null;
    } finally {
      setSavingSlot(false);
    }
    if (!url) {
      fail(FIELDS.PICTURE_UPLOAD_ERROR);
      return false;
    }
    return editRow(row, { imageUrl: url });
  };

  const newStateProposal = (produced) => ({
    kind: PROPOSAL_KINDS.STATE,
    isNew: true,
    targetName: produced.name,
    htmlContent: produced.htmlContent,
    conditions: produced.conditions,
    order: produced.order,
    slotsBefore: null,
    slotsAfter: produced.slotsAfter,
    summary: produced.summary,
  });

  const collect = (item) => setGenerated((current) => [...current, item]);

  const generateFromNote = async (text) => {
    setGenerating(true);
    setBatch((current) => current + 1);
    try {
      const produced = await generateStateFromNote({
        screenVersion: { id: screenVersionId },
        instructions: text,
      });
      if (produced) collect(newStateProposal(produced));
    } catch (error) {
      fail(
        get(error, "message") === UNKNOWN_CONDITION
          ? CHAT.NEW_STATE_NEEDS_CONDITION
          : get(error, "reason") || CHAT.GENERATE_ERROR,
      );
    } finally {
      setGenerating(false);
    }
  };

  const generateFromCapture = async (file, text, asState) => {
    setGenerating(true);
    setBatch((current) => current + 1);
    try {
      const payload = await prepareMockupImage(file);
      if (asState) {
        const produced = await generateState({
          screenVersion: { id: screenVersionId },
          image: payload,
          instructions: text || undefined,
        });
        if (produced) collect(newStateProposal(produced));
        return;
      }
      const nextOrder = (states || []).reduce(
        (max, item) => Math.max(max, item.order || 0),
        0,
      );
      await generateScreenFromCapture(
        { image: payload, instructions: text || undefined },
        {
          generateScreen,
          generateState,
          onScreen: (produced) =>
            collect({
              kind: PROPOSAL_KINDS.VERSION,
              isNew: !hasScreen,
              targetName: defaultLabel,
              htmlContent: produced.html,
              styleContent: produced.css,
              previousHtmlContent: hasScreen ? htmlContent : null,
              previousStyleContent: hasScreen ? styleContent : null,
              slotsBefore: hasScreen ? (slots || []).length : null,
              slotsAfter: produced.slotsAfter,
              summary: produced.summary,
            }),
          onVariant: ({ variant, drawn, index }) =>
            collect({
              kind: PROPOSAL_KINDS.STATE,
              isNew: true,
              targetName: variant.name || drawn.name,
              htmlContent: drawn.htmlContent,
              conditions: variant.conditions || drawn.conditions,
              order: nextOrder + index + 1,
              slotsBefore: null,
              slotsAfter: drawn.slotsAfter,
            }),
          onVariantError: ({ variant, index }) =>
            fail(
              CHAT.STATE_ERROR.replace("{{name}}", variant.name || index + 1),
            ),
        },
      );
    } catch (error) {
      const imageError = {
        [MOCKUP_IMAGE_ERRORS.UNREADABLE]: CHAT.IMAGE_UNREADABLE,
        [MOCKUP_IMAGE_ERRORS.TOO_LARGE]: CHAT.IMAGE_TOO_LARGE,
      }[get(error, "message")];
      fail(imageError || get(error, "reason") || CHAT.GENERATE_ERROR);
    } finally {
      setGenerating(false);
    }
  };

  const startFromMockup = (file) => {
    if (locked || isNil(file)) return Promise.resolve();
    return generateFromCapture(file, "", false);
  };

  const proposeState = ({ text, file }) => {
    const instructions = String(text || "").trim();
    if (locked || !hasScreen) return Promise.resolve();
    if (!isNil(file)) return generateFromCapture(file, instructions, true);
    if (!instructions) return Promise.resolve();
    return generateFromNote(instructions);
  };

  const applyOneGenerated = async (item, target) => {
    if (item.kind === PROPOSAL_KINDS.STATE) {
      const created = createdStates.current.get(item);
      if (created) {
        await writeState({ id: created, htmlContent: item.htmlContent });
        return;
      }
      await createState({
        id: cuid(),
        screenVersionId: target.version.id,
        name: item.targetName,
        order: item.order,
        htmlContent: item.htmlContent,
        conditions: item.conditions,
        onCreated: (id) => createdStates.current.set(item, id),
      });
      return;
    }
    await writeVersion({
      id: target.version.id,
      htmlContent: item.htmlContent,
      styleContent: item.styleContent,
    });
  };

  const createFromGenerated = async (list) => {
    const body = list.find((item) => item.kind === PROPOSAL_KINDS.VERSION);
    const stateItems = list.filter(
      (item) => item.kind === PROPOSAL_KINDS.STATE,
    );
    await createScreenDraft({
      addScreen,
      addScreenVersion,
      detectSlots,
      statusId,
      screenId,
      processId,
      draftOnly,
      description: title || manualScreen,
      versionNumber,
      htmlContent: get(body, "htmlContent", ""),
      styleContent: get(body, "styleContent", ""),
      states: stateItems.map((item) => ({
        name: item.targetName,
        order: item.order,
        htmlContent: item.htmlContent,
        conditions: item.conditions || [],
      })),
      copyStates: stateItems.length > 0,
    });
  };

  const applyGenerated = async (list) => {
    if (!hasScreen) {
      await createFromGenerated(list);
      setGenerated([]);
      return;
    }
    const target = await draftTarget();
    for (const item of list) {
      await applyOneGenerated(item, target);
      setGenerated((current) => current.filter((entry) => entry !== item));
    }
    setGenerated([]);
  };

  const applyProposals = async (kept) => {
    const retained = kept || generated;
    const names = retained
      .map((item) => item.targetName || defaultLabel)
      .join(", ");
    setApplying(true);
    try {
      await applyGenerated(retained);
      done(CHAT.APPLIED.replace("{{targets}}", names));
      setNewStateOpen(false);
    } catch (error) {
      fail(writeError(error, CHAT.GENERATE_ERROR));
    } finally {
      setApplying(false);
    }
    if (reload) reload();
  };

  const discardProposals = () => {
    setGenerated([]);
    inform(CHAT.DISCARDED);
  };

  const handleSaveCode = async () => {
    setSavingCode(true);
    try {
      if (!hasScreen) {
        await createScreenDraft({
          addScreen,
          addScreenVersion,
          detectSlots,
          statusId,
          screenId,
          processId,
          draftOnly,
          description: title || manualScreen,
          versionNumber,
          htmlContent: code.html,
          styleContent: code.css,
        });
        done(CHAT.SCREEN_CREATED);
        setCodeEdited(false);
      } else if (active) {
        const target = await draftTarget();
        await writeState({
          id: target.stateIdOf(active.id),
          htmlContent: code.html,
        });
        done(CHAT.CODE_SAVED.replace("{{target}}", nameOfTarget(activeKey)));
        setCodeEdited(false);
      } else {
        const target = await draftTarget();
        await writeVersion({
          id: target.version.id,
          htmlContent: code.html,
          styleContent: code.css,
          keepEmptyStyle: true,
        });
        done(CHAT.CODE_SAVED.replace("{{target}}", nameOfTarget(activeKey)));
        setCodeEdited(false);
      }
      if (reload) reload();
    } catch (error) {
      fail(writeError(error, CHAT.SAVE_ERROR));
    } finally {
      setSavingCode(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!active || !settings) return;
    setSavingSettings(true);
    try {
      const target = await draftTarget();
      written(
        await updateState({
          variables: {
            where: { id: target.stateIdOf(active.id) },
            data: {
              name: settings.name,
              order: settings.order,
              conditions: settings.conditions || [],
            },
          },
        }),
      );
      done(CHAT.STATE_SAVED.replace("{{name}}", settings.name));
      if (reload) reload();
    } catch (error) {
      fail(writeError(error, CHAT.SAVE_ERROR));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteState = async () => {
    if (!active) return;
    const name = active.name || active.id;
    try {
      const target = await draftTarget();
      written(
        await deleteState({
          variables: { where: { id: target.stateIdOf(active.id) } },
        }),
      );
      pickState(DEFAULT_STATE_KEY, { cite: false, force: true });
      done(CHAT.STATE_DELETED.replace("{{name}}", name));
      if (reload) reload();
    } catch (error) {
      fail(writeError(error, CHAT.SAVE_ERROR));
    }
  };

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const target = await draftTarget();
      written(
        await detectVersion({
          variables: { screenVersion: { id: target.version.id } },
        }),
      );
      for (const state of ordered)
        written(
          await detectForState({
            variables: { screenState: { id: target.stateIdOf(state.id) } },
          }),
        );
      done(WORKSHOP.FIELD_LIST.DETECTED);
      if (reload) reload();
    } catch (error) {
      fail(writeError(error, CHAT.DETECT_ERROR));
    } finally {
      setDetecting(false);
    }
  };

  const problems = reportProblems(report);
  const rows = useMemo(
    () =>
      fieldRows({
        targets,
        known,
        variables,
        formDefinition,
        liveFormDefinition,
        published: publishedScreen,
      }),
    [
      targets,
      known,
      variables,
      formDefinition,
      liveFormDefinition,
      publishedScreen,
    ],
  );
  const resolvedGuide = useMemo(
    () => resolveGuide({ html: shownHtml, slots: shownSlots, guide }),
    [shownHtml, shownSlots, guide],
  );
  const rowOfSlot = (slotId) =>
    rows.find((row) =>
      row.places.some(
        (place) => place.key === activeKey && place.slotId === slotId,
      ),
    ) || null;
  const selectField = useCallback((key) => {
    setGuide(null);
    setOpenKey(key);
  }, []);
  const startGuide = (next) => {
    if (isNil(next)) return false;
    setOpenKey(null);
    const same =
      !isNil(guide) &&
      guide.kind === next.kind &&
      isEqual(guide.path, next.path);
    if (!same) setGuide(next);
    return next;
  };
  const revealPick = (slotId, pick = {}) => {
    const zone = get(resolvedGuide, "zones", []).findIndex(
      (slot) => slot.id === slotId,
    );
    if (zone >= 0 && resolvedGuide.step !== GUIDE_STEPS.SOURCE) {
      setOpenKey(null);
      setGuide({ ...guide, step: GUIDE_STEPS.ZONES, zone });
      return true;
    }
    const sources = { html: shownHtml, slots: shownSlots };
    if (pick.kind === ZONE_KINDS.CARD)
      return startGuide(
        guideForGroup({ ...sources, slotIds: pick.ids, slotId }),
      );
    if (pick.kind === ZONE_KINDS.LIST)
      return startGuide(
        guideForSlot({ ...sources, slotId: slotId || pick.list }),
      );
    if (isNil(slotId)) return false;
    const row = rowOfSlot(slotId);
    if (isNil(row)) return false;
    selectField(row.key);
    return true;
  };
  const openSlotId = () => {
    const row = rows.find((item) => item.key === openKey);
    const place = row
      ? row.places.find((item) => item.key === activeKey)
      : null;
    return place ? place.slotId : null;
  };
  const zones = previewZones({
    slots: shownSlots,
    show: zonesShown,
    labels: { quick: WORKSHOP.QUICK, guide: WORKSHOP.GUIDE, fields: FIELDS },
    guide: resolvedGuide,
    openSlotId: openSlotId(),
    cited,
  });

  const flagged = rows.filter((row) => !isNil(row.problem));
  const issues = hasScreen
    ? flagged.length + problems.length + answers.orphans.length
    : 0;
  const issuesFor = (key) =>
    (key === DEFAULT_STATE_KEY ? problems.length : 0) +
    flagged.filter((row) => row.places.some((place) => place.key === key))
      .length;

  const whenLabels = { labels: STATES, conditionLabels: CONDITIONS };
  const tabs = [
    {
      key: DEFAULT_STATE_KEY,
      name: defaultLabel,
      when: stateWhen(null, variables, whenLabels),
      issues: issuesFor(DEFAULT_STATE_KEY),
    },
    ...ordered.map((state) => ({
      key: state.id,
      name: state.name || state.id,
      when: stateWhen(state, variables, whenLabels),
      issues: issuesFor(state.id),
    })),
  ];

  const cardHtml = useMemo(
    () =>
      mirrorListSlots({
        html: sampleOn && !isNil(sampleRender) ? sampleRender.html : shownHtml,
        sourceHtml: shownHtml,
        slots: shownSlots,
      }),
    [sampleOn, sampleRender, shownHtml, shownSlots],
  );
  const codeDirty = view === WORKSHOP_VIEWS.CODE && codeEdited;
  const leaveReason = ({ stepId, stateId = null }) => {
    const sameStep = stepId === statusId;
    if (sameStep && stateId === activeStateId) return null;
    if (codeDirty) return LEAVE_REASONS.CODE;
    if (sameStep) return null;
    if (generating) return LEAVE_REASONS.GENERATING;
    if (pending && !applying) return LEAVE_REASONS.PROPOSALS;
    return null;
  };
  const leaveRef = useRef(leaveReason);
  leaveRef.current = leaveReason;
  const { guardLeave } = selection;
  useEffect(
    () => guardLeave((target) => leaveRef.current(target)),
    [guardLeave],
  );

  const sampleToggle =
    hasScreen && statusId && !draftOnly
      ? {
          on: sampleOn,
          busy: sampleBusy,
          label: PREVIEW.SAMPLE.LABEL,
          title: sampleOn ? PREVIEW.SAMPLE.HIDE : PREVIEW.SAMPLE.SHOW,
          onToggle: toggleSample,
        }
      : null;

  return {
    hasScreen,
    busy,
    locked,
    issues,
    view: { current: view, open: openView },
    states: {
      tabs,
      active,
      activeKey,
      nameOf: nameOfTarget,
      select: (key) => pickState(key),
    },
    preview: {
      html: cardHtml,
      sourceHtml: shownHtml,
      styleContent,
      slots: shownSlots,
      cited,
      cite: citeSlot,
      select: selectSlot,
      zones,
      showZones: zonesShown,
      toggleZones: () => setZonesShown((shown) => !shown),
    },
    sample: {
      toggle: sampleToggle,
      on: sampleOn && hasScreen && !isNil(sample),
      projectId: get(sample, "project.id"),
      data: get(sample, "data"),
      refreshKey: sampleKey,
      onRendered: setSampleRender,
      fields: sampleOn
        ? {
            answers: get(sample, "answers") || [],
            automations: get(sample, "automations") || [],
            busy: sampleBusy,
            onAnswer: (field, value) =>
              loadSample(prepareSample, { answers: [{ field, value }] }),
            onReset: () => loadSample(resetSample),
          }
        : null,
    },
    code: {
      value: code,
      edited: codeEdited,
      set: editCode,
      save: handleSaveCode,
      saving: savingCode,
      off: busy || savingCode || (!hasScreen && !code.html.trim()),
    },
    settings: {
      value: settings,
      set: setSettings,
      open: settingsOpen && !isNil(active) && !isNil(settings),
      toggle: toggleSettings,
      close: () => setSettingsOpen(false),
      save: handleSaveSettings,
      saving: savingSettings,
      remove: handleDeleteState,
    },
    proposals: {
      list: generated,
      pending,
      generating,
      applying,
      batch,
      apply: applyProposals,
      discard: discardProposals,
      fromMockup: startFromMockup,
      newState: {
        open: newStateOpen,
        setOpen: setNewStateOpen,
        propose: proposeState,
      },
    },
    fields: {
      rows,
      activeKey,
      ordered,
      problems,
      open: openKey,
      select: selectField,
      guide: resolvedGuide,
      setGuide: (patch) =>
        setGuide((current) => (current ? { ...current, ...patch } : current)),
      reveal: revealPick,
      guideFor: (slotId) =>
        startGuide(
          guideForSlot({ html: shownHtml, slots: shownSlots, slotId }),
        ),
      rowOfSlot,
      filter: fieldFilter,
      setFilter: setFieldFilter,
      registry,
      known,
      saving: savingSlot,
      detecting,
      detect: handleDetect,
      edit: editRow,
      createField: handleCreateField,
      editField: handleEditField,
      uploadImage: handleImageUpload,
      citeProblem,
    },
    answers,
  };
};

export default useScreenWorkshop;
